import * as React from 'react';
import { useEffect, useState } from 'react';
import { generateUrl } from '../../../Utilities/api';
// ── Types ──────────────────────────────────────────────────────────────────
import { scoreToBand } from './scoreBand';
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = { red: BRAND.red, yellow: BRAND.yellow, green: BRAND.green };
const BAND_LABELS = { red: 'Handlungsbedarf', yellow: 'Entwicklungspotenzial', green: 'Gut' };
const CATEGORY_LABELS = {
    kraft: 'Kraft', beweglichkeit: 'Beweglichkeit', gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung', schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit', energie: 'Energie', ernaehrung: 'Ernährung',
    mental: 'Mental', environment: 'Umfeld', regeneration: 'Regeneration', training: 'Training',
};
const CATEGORY_ORDER = [
    'kraft', 'beweglichkeit', 'gleichgewicht', 'koerperzusammensetzung',
    'schmerzfreiheit', 'stressfreiheit', 'energie',
    'ernaehrung', 'mental', 'environment', 'regeneration', 'training',
];
const IST_ZUSTAND = ['kraft', 'beweglichkeit', 'gleichgewicht', 'koerperzusammensetzung', 'schmerzfreiheit', 'stressfreiheit', 'energie'];
const VERHALTEN = ['ernaehrung', 'mental', 'environment', 'regeneration', 'training'];
function formatDate(iso) {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
// ── Score Ring ─────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 96, strokeWidth = 8 }) {
    const band = scoreToBand(score);
    const color = BAND_COLORS[band];
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}/>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"/>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.24} fontWeight="700" fill="#111827" style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
                {score}
            </text>
        </svg>);
}
// ── Category Card ──────────────────────────────────────────────────────────
function CategoryCard({ category, entry, isLever, isFocus }) {
    const band = scoreToBand(entry.score);
    const color = BAND_COLORS[band];
    return (<div className="bg-white rounded-2xl p-4 flex flex-col gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', ...(isFocus ? { borderLeft: `3px solid ${BRAND.blue}` } : isLever ? { borderLeft: '3px solid #f59e0b' } : {}) }}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-700">{CATEGORY_LABELS[category] ?? category}</span>
                    {isLever && <span className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>Hebel</span>}
                    {isFocus && <span className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>Fokus</span>}
                </div>
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }}/>
            </div>
            <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${entry.score}%`, backgroundColor: color }}/>
                </div>
                <span className="text-sm font-bold shrink-0" style={{ color: BRAND.blue }}>{entry.score}</span>
            </div>
            {entry.trainerScore !== undefined && entry.fragebogenScore !== undefined && (<div className="grid grid-cols-2 gap-2 text-center border-t border-gray-100 pt-2">
                    <div>
                        <p className="text-xs text-gray-400">Fragebogen</p>
                        <p className="text-sm font-bold" style={{ color: BRAND.blue }}>{entry.fragebogenScore}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400">Messung</p>
                        <p className="text-sm font-bold" style={{ color: BRAND.blue }}>{entry.trainerScore}</p>
                    </div>
                </div>)}
            {entry.interpretation?.customer !== null && entry.interpretation?.customer !== undefined && (<p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-2">
                    {entry.interpretation.customer}
                </p>)}
        </div>);
}
// ── Hebel Card ─────────────────────────────────────────────────────────────
function HebelCard({ leverCategory, measurementTargets, categoryScores }) {
    if (leverCategory === null)
        return null;
    const entry = categoryScores[leverCategory];
    const relevantTargets = measurementTargets.filter(t => t.currentScore < 100);
    return (<div className="rounded-xl p-4 border-l-4" style={{ backgroundColor: BRAND.tint, borderLeftColor: BRAND.blue }}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Hebel-Kategorie</p>
                    <p className="text-base font-bold" style={{ color: BRAND.blueDeep }}>
                        {CATEGORY_LABELS[leverCategory] ?? leverCategory}
                    </p>
                </div>
                {entry && <ScoreRing score={entry.score} size={52} strokeWidth={5}/>}
            </div>
            {relevantTargets.length > 0 && (<div className="flex flex-col gap-1.5 border-t border-blue-100 pt-2 mt-2">
                    <p className="text-xs text-gray-400 mb-1">Messziele</p>
                    {relevantTargets.map(t => (<div key={t.categoryKey} className="flex items-baseline gap-1 text-sm">
                            <span className="text-gray-500 text-xs">{t.label}:</span>
                            <span className="font-bold">{t.currentValue.toFixed(1)}</span>
                            {t.unit && <span className="text-gray-400 text-xs">{t.unit}</span>}
                            {t.targetValue !== null ? (<>
                                    <span className="text-gray-300 mx-0.5">→</span>
                                    <span className="text-green-600 font-bold">{t.targetValue.toFixed(1)}</span>
                                    {t.unit && <span className="text-gray-400 text-xs">{t.unit}</span>}
                                </>) : (<span className="text-green-600 text-xs ml-1">✓ Erreicht</span>)}
                        </div>))}
                </div>)}
        </div>);
}
const HealthscoreReportDetail = ({ uuid, reportsUrl }) => {
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_report', { uuid }), { credentials: 'same-origin' })
            .then(r => {
            if (!r.ok)
                throw new Error();
            return r.json();
        })
            .then(setReport)
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [uuid]);
    if (isLoading) {
        return (<div className="flex flex-col gap-6 animate-pulse">
                <div className="bg-white rounded-xl shadow-sm p-6 h-32"/>
                <div className="h-24 bg-gray-100 rounded-xl"/>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-xl"/>)}
                </div>
            </div>);
    }
    if (isError || report === null) {
        return (<div className="flex flex-col items-center justify-center py-16 gap-4">
                <p className="text-gray-500">Dieser Bericht konnte nicht geladen werden.</p>
                <a href={reportsUrl} className="text-sm font-semibold" style={{ color: BRAND.blue }}>Zur Übersicht</a>
            </div>);
    }
    const band = scoreToBand(report.overallScore);
    const orderedKeys = CATEGORY_ORDER.filter(k => report.categoryScores[k] !== undefined);
    return (<div className="flex flex-col gap-6">

            {/* Header */}
            <div className="bg-white rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                <ScoreRing score={report.overallScore} size={96} strokeWidth={8}/>
                <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Gesamt-Score</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold" style={{ color: BAND_COLORS[band] }}>{report.overallScore}</span>
                        <span className="text-lg font-medium text-gray-400">/ 100</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{BAND_LABELS[band]}</p>
                    <p className="text-xs text-gray-400 mt-2">{formatDate(report.generatedAt)}</p>
                </div>
                <a href={reportsUrl} className="text-sm font-medium whitespace-nowrap" style={{ color: BRAND.blue }}>
                    Alle Berichte
                </a>
            </div>

            {/* Hebel */}
            <HebelCard leverCategory={report.leverCategory} measurementTargets={report.measurementTargets} categoryScores={report.categoryScores}/>

            {/* IST-Zustand */}
            {orderedKeys.filter(k => IST_ZUSTAND.includes(k)).length > 0 && (<div>
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">IST-Zustand</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {orderedKeys.filter(k => IST_ZUSTAND.includes(k)).map(k => (<CategoryCard key={k} category={k} entry={report.categoryScores[k]} isLever={report.leverCategory === k} isFocus={report.focusCategories.includes(k)}/>))}
                    </div>
                </div>)}

            {/* Verhalten */}
            {orderedKeys.filter(k => VERHALTEN.includes(k)).length > 0 && (<div>
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Verhalten</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {orderedKeys.filter(k => VERHALTEN.includes(k)).map(k => (<CategoryCard key={k} category={k} entry={report.categoryScores[k]} isLever={report.leverCategory === k} isFocus={report.focusCategories.includes(k)}/>))}
                    </div>
                </div>)}

        </div>);
};
HealthscoreReportDetail.displayName = 'HealthscoreReportDetail';
export default HealthscoreReportDetail;
